import { doc, setDoc, getDoc, addDoc, collection, serverTimestamp, increment, arrayUnion } from 'firebase/firestore';
import { db } from './firebase';
import type { UtmParams, UserAttribution, InstallCheckOutcome } from '../types/attribution';

const LOCAL_STORAGE_KEY = 'enough_attribution';
const INTERNAL_DEVICE_KEY = 'enough_internal_device';
const AUTOMATION_SESSION_KEY = 'enough_automation_session';

// ============================================
// INTERNAL DEVICE FLAG
// ============================================

/**
 * Mark this device as belonging to an internal/test user. Called once an
 * authenticated user's token carries the isInternalUser claim. Future
 * anonymous sessions on this device write isInternalDevice=true to their
 * attributionVisits docs so marketing metrics can exclude pre-login internal
 * traffic (the Auth claim alone can't cover anonymous visitors).
 */
export const markInternalDevice = (): void => {
    try { localStorage.setItem(INTERNAL_DEVICE_KEY, 'true'); }
    catch { /* ignore */ }
};

export const isInternalDevice = (): boolean => {
    try { return localStorage.getItem(INTERNAL_DEVICE_KEY) === 'true'; }
    catch { return false; }
};

// ============================================
// AUTOMATION SESSION FLAG
// ============================================

/**
 * Mark this browser session as automation (Playwright e2e suites, Claude-loop
 * verification runs, screenshot jobs). Automation boots the app as a fresh
 * anonymous user on every run, so its attribution writes are pure noise —
 * 37.5k of the 41k dev attributionVisits docs on 2026-08-10 — and every
 * automation sign-in to a shared e2e pool account grows that account's
 * previousAnonymousUIDs unboundedly. The write paths below early-return on
 * this flag. Set via a Playwright context init script (e2e fixtures) or the
 * non-prod `?automation=1` URL param (captureAutomationMarker).
 */
export const markAutomationSession = (): void => {
    try { localStorage.setItem(AUTOMATION_SESSION_KEY, 'true'); }
    catch { /* ignore */ }
};

export const isAutomationSession = (): boolean => {
    try { return localStorage.getItem(AUTOMATION_SESSION_KEY) === 'true'; }
    catch { return false; }
};

/**
 * URL opt-in (`?automation=1`) for automation that has no config hook, e.g.
 * MCP-driven Playwright which just navigates a URL. Gated to the same envs as
 * the demo routes (see demoAccess.ts): deployed prod builds ship
 * VITE_APP_ENV='production', so this is inert in production — a real user
 * can never trip it.
 */
export const captureAutomationMarker = (
    appEnv: string | undefined,
    search: string = window.location.search
): void => {
    if (appEnv !== 'development' && appEnv !== 'test') return;
    try {
        if (new URLSearchParams(search).get('automation') === '1') {
            markAutomationSession();
        }
    } catch { /* ignore */ }
};

// ============================================
// UTM PARSING
// ============================================

const UTM_KEYS: (keyof UtmParams)[] = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'
];

/** Parse UTM params from the current URL. Returns null if no UTMs present. */
export const parseUtmParams = (search: string = window.location.search): UtmParams | null => {
    const params = new URLSearchParams(search);
    const utm: UtmParams = {};
    let hasAny = false;

    for (const key of UTM_KEYS) {
        const val = params.get(key);
        if (val) {
            utm[key] = val;
            hasAny = true;
        }
    }

    return hasAny ? utm : null;
};

// ============================================
// PLATFORM DETECTION
// ============================================

const getPlatform = (): 'web' | 'ios' | 'android' => {
    const ua = navigator.userAgent.toLowerCase();
    if (typeof (window as unknown as Record<string, unknown>).Capacitor !== 'undefined') {
        if (ua.includes('iphone') || ua.includes('ipad')) return 'ios';
        if (ua.includes('android')) return 'android';
    }
    return 'web';
};

// ============================================
// LOCAL CACHE (survives before auth resolves)
// ============================================

interface LocalAttribution {
    utm: UtmParams;
    firstUtm: UtmParams;
    landingPage: string;
    referrer: string;
    hubspotutk?: string;
    /** True if the current session brought fresh UTMs (reset on each page load) */
    hasCurrentUtm?: boolean;
}

const getLocalCache = (): LocalAttribution | null => {
    try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
};

const setLocalCache = (data: LocalAttribution): void => {
    try { localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data)); }
    catch { /* ignore */ }
};

/** User has a real account — acquisition UTMs are frozen at signup. */
const isConvertedVisit = (data: Record<string, unknown> | null | undefined): boolean =>
    !!data?.convertedAt || data?.isAnonymous === false;

/** True if there are UTMs or a referrer worth tracking. */
export const hasAttributionSignal = (): boolean => {
    const cached = getLocalCache();
    if (!cached) return false;
    const hasUtm = Object.values(cached.utm).some(v => v);
    const hasReferrer = !!(cached.referrer || document.referrer);
    return hasUtm || hasReferrer;
};

// ============================================
// CAPTURE (called on every app load)
// ============================================

/**
 * Capture attribution data on app entry (localStorage only).
 * Call this on every page load from App.tsx — runs before auth resolves.
 */
export const captureAttribution = (): void => {
    const utm = parseUtmParams();
    const existing = getLocalCache();
    const params = new URLSearchParams(window.location.search);
    const hutk = params.get('hutk') || undefined;

    if (existing) {
        // Reset per-session flag; only true if this page load has fresh UTMs
        existing.hasCurrentUtm = !!utm;
        if (utm) {
            existing.utm = utm;
        }
        // Capture hutk if not already set
        if (hutk && !existing.hubspotutk) {
            existing.hubspotutk = hutk;
        }
        setLocalCache(existing);
    } else {
        // First visit — capture everything
        setLocalCache({
            utm: utm || {},
            firstUtm: utm || {},
            landingPage: window.location.pathname,
            referrer: document.referrer,
            hubspotutk: hutk,
            hasCurrentUtm: !!utm,
        });
    }

    // Strip UTM params and hutk from the URL to prevent double-counting and stale bookmarks
    if (utm || hutk) {
        const url = new URL(window.location.href);
        UTM_KEYS.forEach(key => url.searchParams.delete(key));
        url.searchParams.delete('hutk');
        window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
};

/**
 * Record the visit in Firestore. Call this from AuthContext once auth
 * has settled (anonymous or real user). Safe for every session.
 */
export const recordAttributionVisit = (uid: string, isAnonymous: boolean): void => {
    // Automation sessions are throwaway anonymous users — recording them
    // pollutes dev attribution data (see the flag docs above).
    if (isAutomationSession()) return;
    // Best-effort telemetry; never log in prod (reference-style .catch survives esbuild drop).
    writeVisitDoc(uid, isAnonymous).catch((err) => {
        if (import.meta.env.DEV) console.error('[attribution] writeVisitDoc failed:', err);
    });
};

const writeVisitDoc = async (uid: string, isAnonymous: boolean): Promise<void> => {
    const cached = getLocalCache();
    const platform = getPlatform();

    const hasFreshUtm = !!cached?.hasCurrentUtm;
    const hasAnyUtm = cached ? Object.values(cached.utm).some(v => v) : false;

    const ref = doc(db, 'attributionVisits', uid);
    try {
        const existing = await getDoc(ref);
        const sessionData = {
            utm: hasFreshUtm && cached ? cached.utm : {},
            source: hasFreshUtm && cached ? (cached.utm.utm_source || 'unknown') : 'direct',
            referrer: document.referrer || '',
            landingPage: window.location.pathname,
            platform,
            isAnonymous,
            timestamp: serverTimestamp(),
        };

        if (existing.exists()) {
            // Returning visitor — update summary + add session
            const existingData = existing.data();
            const update: Record<string, unknown> = {
                uid,
                visitCount: increment(1),
                lastVisitAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                isAnonymous,
            };
            // Backfill core fields if a prior write (e.g. installAttribution) created a partial doc
            if (!existingData.platform) update.platform = platform;
            if (!existingData.createdAt) update.createdAt = serverTimestamp();
            if (!existingData.userAgent) update.userAgent = navigator.userAgent;
            if (isInternalDevice()) update.isInternalDevice = true;

            // Last-touch UTMs freeze at signup; later email/web links only go to sessions.
            if (hasFreshUtm && cached && !isConvertedVisit(existingData)) {
                update.utm = cached.utm;
                const existingFirstUtm = existingData.firstUtm || {};
                if (!Object.values(existingFirstUtm).some(v => v)) {
                    update.firstUtm = cached.utm;
                }
            }
            if (cached?.hubspotutk && !existingData.hubspotutk) {
                update.hubspotutk = cached.hubspotutk;
            }

            await setDoc(ref, update, { merge: true });
        } else {
            // First visit — create summary doc
            await setDoc(ref, {
                uid,
                isAnonymous,
                utm: cached?.utm || {},
                firstUtm: hasAnyUtm && cached ? cached.utm : {},
                landingPage: cached?.landingPage || window.location.pathname,
                referrer: cached?.referrer || document.referrer,
                userAgent: navigator.userAgent,
                platform,
                visitCount: 1,
                ...(cached?.hubspotutk ? { hubspotutk: cached.hubspotutk } : {}),
                ...(isInternalDevice() ? { isInternalDevice: true } : {}),
                createdAt: serverTimestamp(),
                lastVisitAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
        }

        // Record individual session
        await addDoc(collection(db, 'attributionVisits', uid, 'sessions'), sessionData);
    } catch (error) {
        console.error('Attribution: failed to write visit doc:', error);
    }
};

// ============================================
// LINK TO USER (called on signup/login)
// ============================================

/**
 * Enrich attribution data when anonymous user converts to a real account.
 * With account linking, the UID stays the same — just update the doc.
 * Also copies attribution to userContent/{uid} for HubSpot sync.
 */
export const linkAttributionToUser = async (uid: string): Promise<void> => {
    // Automation sign-ins (e2e pool accounts, throwaway lifecycle accounts)
    // must not churn conversion state or userContent attribution.
    if (isAutomationSession()) return;
    const cached = getLocalCache();

    try {
        // Update the attribution visit doc to mark as converted
        const visitRef = doc(db, 'attributionVisits', uid);
        const visitSnap = await getDoc(visitRef);
        const visitData = visitSnap.exists() ? visitSnap.data() : null;

        const alreadyConverted = isConvertedVisit(visitData ?? undefined);
        const signupUtmSnapshot =
            visitData?.signupUtm ||
            visitData?.utm ||
            cached?.utm ||
            {};

        const signupUpdate: Record<string, unknown> = {
            isAnonymous: false,
            updatedAt: serverTimestamp(),
        };
        if (!alreadyConverted) {
            signupUpdate.convertedAt = serverTimestamp();
        }
        // Lock last-touch + signup UTMs at first conversion (never overwrite after)
        if (!visitData?.signupUtm) {
            signupUpdate.signupUtm = signupUtmSnapshot;
            signupUpdate.signupReferrer = visitData?.referrer || cached?.referrer || '';
            signupUpdate.utm = signupUtmSnapshot;
            signupUpdate.attributionLockedAt = serverTimestamp();
        }

        await setDoc(visitRef, signupUpdate, { merge: true });

        const lockedUtm = (visitData?.signupUtm || signupUtmSnapshot) as UtmParams;

        // Copy attribution to userContent for HubSpot sync trigger
        const attribution: UserAttribution = {
            visitorId: uid,
            utm: lockedUtm,
            firstUtm: cached?.firstUtm || {},
            landingPage: cached?.landingPage || '',
            referrer: cached?.referrer || '',
            platform: getPlatform(),
            visitCount: 0, // will be filled from visit doc
            ...(cached?.hubspotutk ? { hubspotutk: cached.hubspotutk } : {}),
        };

        // Use Firestore data as source of truth over local cache
        if (visitData) {
            attribution.visitCount = visitData.visitCount || 0;
            if (visitData.firstUtm) attribution.firstUtm = visitData.firstUtm;
            if (visitData.signupUtm) {
                attribution.utm = visitData.signupUtm as UtmParams;
            } else if (visitData.utm) {
                attribution.utm = visitData.utm as UtmParams;
            }
            if (visitData.landingPage) attribution.landingPage = visitData.landingPage;
            if (visitData.referrer) attribution.referrer = visitData.referrer;
            if (visitData.hubspotutk) attribution.hubspotutk = visitData.hubspotutk;
        }

        await setDoc(doc(db, 'userContent', uid), {
            attribution,
            updatedAt: serverTimestamp(),
        }, { merge: true });

    } catch (error) {
        console.error('Attribution: failed to link to user:', error);
    }
};

/**
 * Snapshot the current UTM state at first paid subscription (immutable milestone).
 * Call from SubscriptionContext when a free→paid transition is detected.
 */
export const recordSubscriptionAttribution = async (uid: string): Promise<void> => {
    // The e2e pool accounts are entitled members with existing attributionVisits
    // docs, so features-suite runs would reach the write below; suppress like
    // every other write path in this service.
    if (isAutomationSession()) return;
    try {
        const ref = doc(db, 'attributionVisits', uid);
        const snap = await getDoc(ref);
        if (snap.exists() && !snap.data().subscriptionUtm) {
            const data = snap.data();
            const snapshotUtm = data.signupUtm || data.utm || {};
            await setDoc(ref, {
                subscriptionUtm: snapshotUtm,
                subscribedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            }, { merge: true });
        }
    } catch (error) {
        console.error('Attribution: failed to record subscription attribution:', error);
    }
};

/**
 * Classify an install-attribution attempt into a coarse outcome bucket so capture
 * success becomes measurable. Honors an explicit `checkOutcome` from the plugin when
 * present (forward-compat with native retry work); otherwise derives it from the result.
 */
export const classifyInstallOutcome = (data: Record<string, unknown>): InstallCheckOutcome => {
    if (data.attributed === true) return 'attributed';
    const explicit = data.checkOutcome;
    if (explicit === 'organic' || explicit === 'unavailable' || explicit === 'error') {
        return explicit;
    }
    // No native attribution surface (web stub, or platform API returned nothing usable).
    if (data.source === 'web') return 'unavailable';
    // Native check ran and simply found no paid referrer → organic install.
    return 'organic';
};

/**
 * Store install attribution from native platform APIs (Play Install Referrer, Apple Search Ads).
 * Call once on first native app launch. Writes to attributionVisits/{uid}.installAttribution.
 *
 * Records a lightweight outcome marker (`installAttribution.checkOutcome` + `checkedAt`) for
 * EVERY attempt, not just paid installs, so the never-opened / capture-failure / over-reporting
 * buckets become separable. The paid payload written on `attributed:true` is unchanged from
 * before, so `_is_paid_install_attribution` and the appInstalls reconciliation read identically.
 */
export const recordInstallAttribution = async (
    uid: string,
    data: Record<string, unknown>
): Promise<void> => {
    // Automation sessions must not create/annotate attributionVisits docs
    // (this path can seed a skeleton doc when it wins the race with
    // recordAttributionVisit).
    if (isAutomationSession()) return;
    const checkOutcome = classifyInstallOutcome(data);
    try {
        const ref = doc(db, 'attributionVisits', uid);
        const existing = await getDoc(ref);
        const existingData = existing.exists() ? existing.data() : null;

        // Never overwrite a converted visitor's frozen attribution.
        if (existingData && isConvertedVisit(existingData)) {
            return;
        }
        // Preserve a previously captured paid attribution: a later session whose check
        // comes back organic/unavailable/error must not clobber the attributed payload.
        const priorIa = existingData?.installAttribution as Record<string, unknown> | undefined;
        if (checkOutcome !== 'attributed' && priorIa?.attributed === true) {
            return;
        }

        // On a successful attribution, keep the full paid payload exactly as before and
        // annotate it. Otherwise store only the lightweight outcome marker. We deliberately
        // do NOT persist `source` on the non-attributed marker: the iOS plugin hard-codes
        // source="apple-search-ads" on every result (incl. organic), and downstream readers
        // (e.g. HubSpot) historically treat that source as a paid signal — storing it here
        // would mislabel organic iOS installs as paid ASA. `checkOutcome` already records the
        // outcome bucket, and the visit doc's `platform` field carries the per-platform split.
        const installAttribution = checkOutcome === 'attributed'
            ? { ...data, checkOutcome, checkedAt: serverTimestamp() }
            : {
                attributed: false,
                checkOutcome,
                checkedAt: serverTimestamp(),
            };

        const update: Record<string, unknown> = {
            installAttribution,
            updatedAt: serverTimestamp(),
        };
        // If this write creates the doc (race with recordAttributionVisit),
        // include core fields so the doc isn't a skeleton.
        if (!existing.exists()) {
            update.uid = uid;
            update.platform = getPlatform();
            update.createdAt = serverTimestamp();
            update.isAnonymous = true;
            update.visitCount = 0;
        }
        await setDoc(ref, update, { merge: true });
    } catch (error) {
        console.error('Attribution: failed to record install attribution:', error);
    }
};

/**
 * Associate an anonymous visitor with an authenticated UID.
 * Called when a user who was browsing anonymously logs into an existing account
 * (different UID). Writes the correlation to the authenticated user's own
 * attributionVisits doc (which they have permission to write) rather than
 * cross-writing to the anonymous user's doc.
 */
export const associateAnonymousVisitor = async (
    anonymousUid: string,
    authenticatedUid: string
): Promise<void> => {
    if (!anonymousUid || !authenticatedUid || anonymousUid === authenticatedUid) return;
    // Automation sign-ins to the shared e2e pool accounts would otherwise grow
    // previousAnonymousUIDs by one throwaway anon uid per run (4.4k–6.3k per
    // pool account by 2026-08-10 — enough to 504 the funnel read).
    if (isAutomationSession()) return;
    try {
        // Write to the authenticated user's own doc — no cross-UID permission needed
        await setDoc(doc(db, 'attributionVisits', authenticatedUid), {
            previousAnonymousUIDs: arrayUnion(anonymousUid),
            updatedAt: serverTimestamp(),
        }, { merge: true });
    } catch (error) {
        console.error('Attribution: failed to associate anonymous visitor:', error);
    }
};

/**
 * Get attribution data for a user from Firestore.
 */
export const getUserAttribution = async (uid: string): Promise<UserAttribution | null> => {
    try {
        const snap = await getDoc(doc(db, 'userContent', uid));
        return snap.data()?.attribution || null;
    } catch {
        return null;
    }
};
